import {
  Button,
  calendarDay,
  dayRange,
  formatDateTime,
  ListView,
  PageHeader,
  TextButton,
  useCursorList,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { FilterToolbar } from "@majitap/ui/arc/filter-toolbar/filter-toolbar";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { type Incident, type IncidentType, incidentTypesQuery } from "../../incidents";
import { DELIVERY_STATUS, type DeliveryStatus } from "../../labels";
import { filterSearch } from "../../list-search";
import styles from "../dispatch/dispatch.module.css";
import { IncidentTypeDialog, LogIncidentDialog, ResolveDialog } from "./dialogs";

export const incidentFilters = filterSearch({
  show: ["open", "resolved", "all"] as const,
  type_code: "text",
  delivery_id: "text",
  from: "text",
  to: "text",
});

/** Incidents (P8-09, `ONBhs` in the design): problems on deliveries, open first, and the types to log them with. */
export function IncidentsPage(): ReactNode {
  const search = useSearch({ from: "/console/incidents" });
  const navigate = useNavigate({ from: "/incidents" });
  const format = useCompanyFormat();
  const types = useQuery(incidentTypesQuery);
  const [dialog, setDialog] = useState<
    { kind: "log" } | { kind: "resolve"; incident: Incident } | { kind: "type"; type?: IncidentType } | null
  >(null);
  const show = search.show ?? "open";
  const query = {
    resolved: show === "all" ? undefined : show === "resolved" ? ("true" as const) : ("false" as const),
    type_code: search.type_code,
    delivery_id: search.delivery_id,
    from: search.from,
    to: search.to,
  } as const;
  const list = useCursorList({
    queryKey: ["incidents", query],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/incidents", {
        params: { query: { ...query, cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const set = (patch: Partial<typeof search>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const chips = [
    ...(search.type_code
      ? [
          {
            id: "type_code",
            label: "Type",
            value: types.data?.find((t) => t.code === search.type_code)?.name ?? search.type_code,
          },
        ]
      : []),
    ...(search.delivery_id
      ? [{ id: "delivery_id", label: "Delivery", value: list.rows[0]?.delivery_number ?? "chosen" }]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Incidents"
        description="Problems on deliveries, open and resolved."
        actions={<Button onClick={() => setDialog({ kind: "log" })}>Log an incident</Button>}
      />
      <ListView
        caption="Incidents"
        itemName={{ one: "incident", other: "incidents" }}
        rows={list.rows}
        rowKey={(i) => i.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={chips.length > 0 || Boolean(search.from)}
        onResetFilters={() => navigate({ search: { show: search.show } })}
        empty={
          show === "open"
            ? {
                title: "Nothing open",
                description: "Incidents reported by drivers or dispatchers show here until they're resolved.",
              }
            : { title: "No incidents", description: "Incidents reported by drivers or dispatchers show here." }
        }
        toolbar={
          <>
            <SegmentedControl
              label="Open or resolved"
              value={show}
              onValueChange={(v) => set({ show: v as "open" | "resolved" | "all" })}
              options={[
                { value: "open", label: "Open" },
                { value: "resolved", label: "Resolved" },
                { value: "all", label: "All" },
              ]}
            />
            <div className={styles.toolbarEnd}>
              <DateRangePicker
                label="Reported"
                placeholder="Any date"
                value={
                  search.from && search.to
                    ? {
                        start: calendarDay(search.from, format.timezone),
                        end: calendarDay(new Date(Date.parse(search.to) - 1).toISOString(), format.timezone),
                      }
                    : null
                }
                onChange={(range) => set(dayRange(range, format.timezone))}
              />
              <FilterToolbar
                filters={chips}
                onRemove={(id) => set({ [id]: undefined })}
                onClearAll={() => navigate({ search: { show: search.show } })}
                addFilter={{
                  fields: [
                    {
                      id: "type_code",
                      label: "Type",
                      options: (types.data ?? []).map((t) => ({ value: t.code, label: t.name })),
                    },
                  ],
                  onAdd: (chip) => set({ type_code: chip.value }),
                }}
              />
            </div>
          </>
        }
        columns={[
          {
            key: "created_at",
            label: "Reported",
            render: (i) => (
              <span className={styles.stack}>
                <span>{formatDateTime(i.created_at, format)}</span>
                {i.reported_by?.name ? <span className={styles.muted}>by {i.reported_by.name}</span> : null}
              </span>
            ),
          },
          {
            key: "type",
            label: "Type",
            render: (i) => <Badge tone={i.resolved_at ? "neutral" : "warning"}>{i.type.name}</Badge>,
          },
          {
            key: "delivery_number",
            label: "Delivery",
            render: (i) => (
              <span className={styles.stack}>
                <span>{i.delivery_number}</span>
                <span className={styles.muted}>
                  {DELIVERY_STATUS[i.delivery_status as DeliveryStatus]?.[0] ?? i.delivery_status}
                </span>
              </span>
            ),
          },
          {
            key: "notes",
            label: "Notes",
            render: (i) => (
              <span className={styles.stack}>
                <span>{i.notes ?? "—"}</span>
                {i.resolution_notes ? <span className={styles.muted}>Resolved: {i.resolution_notes}</span> : null}
              </span>
            ),
          },
          {
            key: "actions",
            label: "",
            render: (i) =>
              i.resolved_at ? (
                <span className={styles.muted}>
                  {formatDateTime(i.resolved_at, format)}
                  {i.resolved_by?.name ? ` by ${i.resolved_by.name}` : ""}
                </span>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "resolve", incident: i })}>
                  Resolve
                </Button>
              ),
          },
        ]}
      />

      <section className={styles.step}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Incident types</h2>
          <TextButton onClick={() => setDialog({ kind: "type" })}>Add a type</TextButton>
        </div>
        <div className={styles.toolbarEnd}>
          {(types.data ?? []).map((t) => (
            <Button key={t.id} variant="secondary" size="sm" onClick={() => setDialog({ kind: "type", type: t })}>
              {t.is_active ? t.name : `${t.name} (off)`}
            </Button>
          ))}
        </div>
      </section>

      {dialog?.kind === "log" ? <LogIncidentDialog onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "resolve" ? <ResolveDialog incident={dialog.incident} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "type" ? <IncidentTypeDialog type={dialog.type} onClose={() => setDialog(null)} /> : null}
    </>
  );
}
